import { useMemo } from "react";
import {
  CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis,
} from "recharts";
import { StatusBadge } from "@/components/wheel/StatusBadge";
import { CockpitPanel, useCockpitAdmin } from "@/cockpit/CockpitPanel";
import { FixedChart } from "@/cockpit/FixedChart";
import { FlowSplitBar } from "@/cockpit/FlowSplitBar";
import { downsampleSeries } from "@/cockpit/downsample";
import { AXIS, DEM, GOP, GRID, compactUsd, fmtMd, tooltipBox } from "@/cockpit/formatCockpit";
import { PartyDot } from "@/cockpit/chartMarks";

function PartyLegend({ payload }) {
  return (
    <div className="flex flex-wrap gap-3 justify-center text-[10px] pt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
      {(payload || []).map((p) => {
        const dem = p.dataKey === "democratNetUsdEstimate";
        return (
          <span key={p.dataKey} className="inline-flex items-center gap-1.5" style={{ color: p.color }}>
            {dem
              ? <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }} />
              : <span className="inline-block w-2 h-2" style={{ background: p.color }} />}
            <span aria-hidden="true">{dem ? "D" : "R"}</span>
            {dem ? "Dem net (est.) · solid" : "Rep net (est.) · dashed"}
          </span>
        );
      })}
    </div>
  );
}

export function groupPoliticians(trades) {
  const map = new Map();
  (trades || []).forEach((t) => {
    const key = t.politicianName;
    const row = map.get(key) || {
      politicianName: t.politicianName,
      party: t.party,
      chamber: t.chamber,
      buyUsdEstimate: 0,
      sellUsdEstimate: 0,
      count: 0,
      delayFlag: false,
      maxDelayDays: 0,
    };
    if (t.type === "Sell") row.sellUsdEstimate += Number(t.amountMidpointUsd) || 0;
    else row.buyUsdEstimate += Number(t.amountMidpointUsd) || 0;
    row.count += 1;
    if (t.delayFlag) row.delayFlag = true;
    row.maxDelayDays = Math.max(row.maxDelayDays, t.delayDays || 0);
    map.set(key, row);
  });
  return [...map.values()].sort(
    (a, b) => (b.buyUsdEstimate + b.sellUsdEstimate) - (a.buyUsdEstimate + a.sellUsdEstimate),
  );
}

export function PoliticalFlowTimeline({ timeline, trades, onOpenRaw }) {
  const isAdmin = useCockpitAdmin();
  const data = useMemo(() => downsampleSeries(timeline || [], 500), [timeline]);
  const grouped = useMemo(() => groupPoliticians(trades), [trades]);
  const sparseDot = (party) => (props) => {
    const i = props.index;
    if (i !== 0 && i !== (data.length - 1) && i % 18 !== 0) return null;
    return <PartyDot {...props} party={party} />;
  };

  return (
    <CockpitPanel
      title="Congressional PTR"
      hint="Grouped by politician. Amounts are STOCK Act ranges (bracket midpoint for the split bar). Party D/R split is not in the house-feed snapshot."
      onOpenRaw={onOpenRaw}
      testid="political-flow-timeline"
      footer={<PtrGroupedTable rows={grouped} />}
    >
      {data.length > 0 ? (
        <FixedChart height={200}>
          {(width) => (
            <LineChart width={width} height={200} data={data} onClick={onOpenRaw} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: AXIS, fontSize: 9 }} tickFormatter={fmtMd} minTickGap={28} />
              <YAxis tick={{ fill: AXIS, fontSize: 9 }} tickFormatter={compactUsd} width={52} />
              <Tooltip
                contentStyle={tooltipBox}
                labelFormatter={(l) => `${l} · rolling 30d`}
                formatter={(v, name) => [
                  compactUsd(v),
                  name === "democratNetUsdEstimate" ? "D · Democrat net (est.)" : "R · Republican net (est.)",
                ]}
              />
              <Legend content={<PartyLegend />} />
              <Line
                dataKey="democratNetUsdEstimate"
                name="Democrat net (est.)"
                stroke={DEM}
                dot={sparseDot("D")}
                strokeWidth={1.6}
                isAnimationActive={false}
              />
              <Line
                dataKey="republicanNetUsdEstimate"
                name="Republican net (est.)"
                stroke={GOP}
                strokeDasharray="5 4"
                dot={sparseDot("R")}
                strokeWidth={1.6}
                isAnimationActive={false}
              />
            </LineChart>
          )}
        </FixedChart>
      ) : (
        isAdmin ? (
        <p className="text-[10px] mb-1" style={{ color: "var(--hoip-text-tertiary)" }}>
          No Democrat/Republican time series in Mongo. Showing grouped PTR rows from the house feed.
        </p>
        ) : null
      )}
    </CockpitPanel>
  );
}

function initials(name) {
  return String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function PtrGroupedTable({ rows }) {
  const isAdmin = useCockpitAdmin();
  if (!rows.length) return null;
  return (
    <div className="mt-2 space-y-1.5 overflow-x-auto min-w-0" data-testid="ptr-grouped-table">
      <div className="flex items-center text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
        <span className="w-[38%]">Politician</span>
        <span className="w-[16%]">Chamber</span>
        <span className="flex-1">▲ Buy vs ▼ Sell (est.)</span>
        <span className="w-[18%] text-right">Est. volume</span>
      </div>
      {rows.map((t) => (
        <div key={t.politicianName} className="flex items-center gap-2 text-[10px]">
          <span className="w-[38%] min-w-0 inline-flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full inline-flex items-center justify-center text-[8px] font-bold shrink-0"
              style={{ background: "var(--hoip-hover)", color: "var(--hoip-text-secondary)" }}>
              {initials(t.politicianName)}
            </span>
            <span className="truncate" title={`${t.politicianName} · ${t.party || "party n/a"} · ${t.count} filings`}>
              {t.politicianName}
            </span>
            {t.party ? (
              <span className="text-[8px] shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>{t.party}</span>
            ) : null}
          </span>
          <span className="w-[16%] shrink-0">{t.chamber}</span>
          <FlowSplitBar
            leftUsd={t.buyUsdEstimate}
            rightUsd={t.sellUsdEstimate}
            leftLabel="▲ Buy"
            rightLabel="▼ Sell"
            title={isAdmin ? "Bracket-midpoint estimates, not exact dollars" : undefined}
          />
          <span
            className="w-[18%] text-right font-mono-data shrink-0"
            title={isAdmin ? "Sum of STOCK Act bracket midpoints in this window. Exact dollars are not disclosed." : undefined}
          >
            {compactUsd(t.buyUsdEstimate + t.sellUsdEstimate)}
          </span>
          {t.delayFlag ? (
            <StatusBadge
              tier="caution"
              text={`${t.maxDelayDays}d`}
              title="At least one filing in this window was disclosed more than 30 days after the trade."
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}
